import type {
  Account,
  AccountCreate,
  AccountList,
  AccountListQuery,
  AccessGrant,
  AccessPreview,
  AccessRequest,
  AdminUser,
  AdminUserList,
  ApiErrorBody,
  AuditList,
  AuthConfig,
  ConsoleConfig,
  ConsoleKeyCreate,
  ConsoleKeyList,
  Consent,
  ConsentCreate,
  ConsentList,
  CreatedConsoleKey,
  Dataset,
  DatasetCreate,
  DatasetEventList,
  DatasetList,
  DatasetListQuery,
  DatasetUpdate,
  DeletionRequest,
  DeletionRequestCreate,
  DisableRequest,
  Evaluation,
  EvaluationCreate,
  EvaluationList,
  FetchLines,
  Me,
  MeUpdate,
  PauseRequest,
  PauseState,
  Purpose,
  ReadList,
  SecondFactorCode,
  SecondFactorConfirmed,
  SecondFactorEnrolment,
  StorageHealth,
  Target,
  TargetCreate,
  TargetList,
  Version,
  VersionComplete,
  VersionCreate,
  VersionList,
} from "./types";

/** Base path of the versioned API, matching `docs/API.md`. */
export const DEFAULT_BASE_URL = "/api/v1";

/**
 * Header every mutating request carries (`docs/AUTH.md` §1).
 *
 * The session is a cookie, so a cross-site form could otherwise post as the
 * person who is signed in. A custom header cannot be set by such a form
 * without CORS, which the server does not grant — belt and braces on top of
 * `SameSite=Lax`. A bearer request does not need it, because that header
 * defends against a credential the browser attaches by itself.
 */
export const CSRF_HEADER = "X-Data-Console";

/** Methods that change something and therefore need the CSRF header. */
const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Error raised for every non-2xx API response. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | null;

  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** The record does not exist — an unknown id, or a route that has no answer. */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * The console was never told something it is being asked about.
   *
   * No endpoint, no bucket, no encryption key for second factors. The
   * catalogue keeps working; what is missing is a setting, and the screen says
   * whose it is.
   */
  get isNotConnected(): boolean {
    return this.status === 503 || this.code === "not_connected";
  }

  /** Secure storage is configured but refused or failed. The bytes are unreachable. */
  get isStorageUnavailable(): boolean {
    return this.status === 502 || this.code === "object_store_unavailable";
  }

  /** There is no session, or it expired: signing in again is the fix. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /**
   * Authenticated, but not allowed to do this.
   *
   * Signing in again would not help, so the interface must never offer it as
   * the fix.
   */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** Signed in, but nobody has approved this account yet. */
  get isAccountPending(): boolean {
    return this.status === 403 && this.code === "account_pending";
  }

  /** Signed in, but the account has been turned off. */
  get isAccountDisabled(): boolean {
    return this.status === 403 && this.code === "account_disabled";
  }

  /**
   * The act needs your code as well as your cookie.
   *
   * Four acts ask for it: widening a consent, requesting a deletion,
   * approving an account, minting a console key. `details.enrolled` says
   * whether there is a factor to prove against, so the screen can offer
   * enrolment instead of a code box.
   */
  get isStepUpRequired(): boolean {
    return this.status === 403 && this.code === "step_up_required";
  }

  /** Whether the caller has a second factor at all, as the refusal reported it. */
  get isEnrolled(): boolean {
    return this.details?.["enrolled"] !== false;
  }

  /** The client's consent does not cover the purpose that was declared. */
  get isPurposeNotPermitted(): boolean {
    return this.status === 403 && this.code === "purpose_not_permitted";
  }

  /** A `dk_` key reaching outside the accounts it was minted for. */
  get isKeyNotScoped(): boolean {
    return this.status === 403 && this.code === "key_not_scoped";
  }

  /** The bytes are not there to be read; `details.state` says which of the four. */
  get isVersionNotAvailable(): boolean {
    return this.status === 409 && this.code === "version_not_available";
  }

  /** The operator's emergency stop. Nothing already recorded is affected. */
  get isConsolePaused(): boolean {
    return this.status === 403 && this.code === "console_paused";
  }

  // There is deliberately no `isQuotaExceeded`, and there never will be one.
  // `docs/API.md` has a paragraph saying this API carries no `quota_exceeded`,
  // no `too_many_datasets` and no `storage_full`: what bounds the bill is a
  // file, a bucket lifecycle, a credential and the bill itself, and each names
  // itself when it refuses. A client path for a code nothing returns is dead
  // code that teaches the next reader the wrong model.
  //
  // There is no `isSessionRequired` either, for a different reason: every
  // route this browser calls is on the session side of the allow-list, so
  // `403 session_required` is an answer only a `dk_` key can provoke. If one
  // ever reaches this bundle it is a bug, and the generic forbidden branch
  // quoting the server's own sentence is the honest way to show it.
}

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  method?: HttpMethod;
  /** Serialized as JSON when present. */
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

/**
 * What the server said about *who* is calling, rather than about the request.
 *
 * - `unauthenticated`: no session at all → the sign-in screen.
 * - `account_blocked`: a real session whose account may not act yet (pending)
 *   or any more (disabled) → the account screen, after re-reading `/me`.
 */
export type AuthEvent = "unauthenticated" | "account_blocked";

export interface ApiClientOptions {
  /** Base URL for the versioned API. */
  baseUrl?: string;
  /**
   * Extra identity headers, resolved per request.
   *
   * Empty with Google sign-in: the identity is an HttpOnly cookie the browser
   * attaches itself. It only ever carries `X-User` in the server's documented
   * development mode, which is not authentication and says so on screen.
   */
  identityHeaders?: () => Record<string, string>;
  /** Notified whenever the server refuses the caller rather than the call. */
  onAuthEvent?: (event: AuthEvent) => void;
  /** Injectable for tests. */
  fetchImpl?: typeof fetch;
}

/** Typed HTTP client for the Data Console API (`docs/API.md`). */
export interface ApiClient {
  /** How this deployment signs people in; served without a session. */
  getAuthConfig(signal?: AbortSignal): Promise<AuthConfig>;
  /** Clear the session cookie and end it server-side; then reload the app. */
  logout(): Promise<void>;
  /** Every bound, list and default a form would otherwise invent. */
  getConfig(signal?: AbortSignal): Promise<ConsoleConfig>;
  getMe(signal?: AbortSignal): Promise<Me>;
  /** The one field on this surface a person owns. */
  updateMe(payload: MeUpdate): Promise<Me>;
  /** Start setting up your code; the only answer that carries the secret. */
  enrolSecondFactor(): Promise<SecondFactorEnrolment>;
  /** Confirm the enrolment, or open the 15-minute window before a gated act. */
  verifySecondFactor(payload: SecondFactorCode): Promise<SecondFactorConfirmed>;
  /** Remove your code; a current code is required in the body. */
  removeSecondFactor(payload: SecondFactorCode): Promise<void>;
  /** The console keys that can reach the catalogue right now, newest first. */
  listConsoleKeys(signal?: AbortSignal): Promise<ConsoleKeyList>;
  /**
   * Mint a console key for a workspace.
   *
   * The answer is the only place its secret ever exists outside the holder's
   * own terminal: it is shown once and never stored here. Asks for your code.
   */
  createConsoleKey(payload: ConsoleKeyCreate): Promise<CreatedConsoleKey>;
  /** Revoke one of your own keys; it is dead at its next request. */
  revokeConsoleKey(id: string): Promise<void>;
  /** The client companies we hold data for, by name. */
  listAccounts(
    query?: AccountListQuery,
    signal?: AbortSignal,
  ): Promise<AccountList>;
  /** Add a client company. Its short name is frozen from this moment. */
  createAccount(payload: AccountCreate): Promise<Account>;
  getAccount(id: string, signal?: AbortSignal): Promise<Account>;
  listDatasets(
    query?: DatasetListQuery,
    signal?: AbortSignal,
  ): Promise<DatasetList>;
  /** Record a dataset, its first consent and — if there is one — its first bar. */
  createDataset(payload: DatasetCreate): Promise<Dataset>;
  /**
   * One dataset, whole — or with its trace narrowed to what is new.
   *
   * `events_after` asks for the lines after an index already held, so a page
   * open during a large upload reads one new sentence instead of the trace.
   */
  getDataset(
    id: string,
    eventsAfter?: number,
    signal?: AbortSignal,
  ): Promise<Dataset>;
  /** Correct a declared field. The correction is written into the trace. */
  updateDataset(id: string, payload: DatasetUpdate): Promise<Dataset>;
  /** The whole trace, or the part of it after an index already held. */
  listEvents(
    id: string,
    eventsAfter?: number,
    signal?: AbortSignal,
  ): Promise<DatasetEventList>;
  /** The whole consent ledger, newest first, superseded rows included. */
  listConsent(id: string, signal?: AbortSignal): Promise<ConsentList>;
  /** Record the whole new permission list. Widening asks for your code. */
  recordConsent(id: string, payload: ConsentCreate): Promise<Consent>;
  /** The bar's own history: it moves during a negotiation. */
  listTargets(id: string, signal?: AbortSignal): Promise<TargetList>;
  /** Record a bar. The direction comes from the metric table, never the body. */
  recordTarget(id: string, payload: TargetCreate): Promise<Target>;
  /** Every attempt against the bar, including the ones that did not clear it. */
  listEvaluations(id: string, signal?: AbortSignal): Promise<EvaluationList>;
  /** Record a result somebody measured elsewhere, with the run it came from. */
  recordEvaluation(id: string, payload: EvaluationCreate): Promise<Evaluation>;
  listVersions(id: string, signal?: AbortSignal): Promise<VersionList>;
  getVersion(
    id: string,
    number: number,
    signal?: AbortSignal,
  ): Promise<Version>;
  /**
   * Open a version and get the part URLs the browser writes with.
   *
   * Idempotent while the version is waiting for its bytes: called again it
   * returns the same upload and fresh URLs for the parts not yet accepted,
   * which is how a browser whose URLs went stale carries on.
   */
  createVersion(id: string, payload: VersionCreate): Promise<Version>;
  /** Seal the file: only the console does this, and only once. */
  completeVersion(
    id: string,
    number: number,
    payload: VersionComplete,
  ): Promise<Version>;
  /** Ask for a link, naming why. Every attempt is written down. */
  requestAccess(
    id: string,
    number: number,
    payload: AccessRequest,
  ): Promise<AccessGrant>;
  /** The same decision, and nothing written: would this request be taken? */
  previewAccess(
    id: string,
    number: number,
    payload: AccessRequest,
  ): Promise<AccessPreview>;
  /** The console's own copy-paste lines for this version. */
  getFetchLines(
    id: string,
    number: number,
    purpose?: Purpose,
    signal?: AbortSignal,
  ): Promise<FetchLines>;
  /** Every read of this client's data: granted and refused alike. */
  listReads(
    id: string,
    after?: string,
    signal?: AbortSignal,
  ): Promise<ReadList>;
  /** Ask for the bytes to be destroyed. Asks for your code; 24 hours out. */
  requestDeletion(
    id: string,
    payload: DeletionRequestCreate,
  ): Promise<DeletionRequest>;
  /** The scheduled request, or the receipt once it completed. */
  getDeletion(id: string, signal?: AbortSignal): Promise<DeletionRequest>;
  /** Cancel inside the window. Keeping a client's file one more day is safe. */
  cancelDeletion(id: string): Promise<void>;
  listUsers(signal?: AbortSignal): Promise<AdminUserList>;
  /** Approve an account. Asks for your code: it hands over the whole catalogue. */
  approveUser(username: string): Promise<AdminUser | null>;
  /** Turn an account off; every console key it holds stops at its next request. */
  disableUser(
    username: string,
    payload: DisableRequest,
  ): Promise<AdminUser | null>;
  getAudit(
    limit?: number,
    after?: string,
    signal?: AbortSignal,
  ): Promise<AuditList>;
  /** The emergency stop: no new datasets, uploads or links. It deletes nothing. */
  setPaused(payload: PauseRequest): Promise<PauseState | null>;
  /** Storage health, and the honest one: what could not be read says so. */
  getStorageHealth(signal?: AbortSignal): Promise<StorageHealth>;

  // There is deliberately no `downloadVersion` and no route that returns bytes:
  // the console hands out single-object links with a clock on them and never
  // proxies a client's file. And there is no admin route for somebody else's
  // console keys — `docs/API.md` says an admin's control is disabling the
  // account, which stops every key it holds at its next request.
}

function buildUrl(
  baseUrl: string,
  path: string,
  query: RequestOptions["query"],
): string {
  const url = `${baseUrl}${path}`;
  if (!query) {
    return url;
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) {
      params.set(key, String(value));
    }
  }
  const serialized = params.toString();
  return serialized ? `${url}?${serialized}` : url;
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Partial<ApiErrorBody>;
  return (
    typeof candidate.error === "string" && typeof candidate.message === "string"
  );
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (isErrorBody(body)) {
    return new ApiError(
      response.status,
      body.error,
      body.message,
      body.details ?? null,
    );
  }
  return new ApiError(
    response.status,
    "http_error",
    `The server answered ${response.status} ${response.statusText}`.trim(),
  );
}

/**
 * Create a typed API client.
 *
 * Every call sends the session cookie (`credentials: "include"`), and every
 * mutation sends the CSRF header the API requires. Any non-2xx answer becomes
 * an `ApiError`; the two that are about the caller rather than the call — no
 * session, or an account that may not act — are also reported through
 * `onAuthEvent` so the application can drop to the right screen from anywhere.
 *
 * Args:
 *     options: Base URL, identity-header resolver, authentication-event
 *         callback and optional `fetch` override used by tests.
 *
 * Returns:
 *     An `ApiClient` raising `ApiError` on any non-2xx response.
 */
export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = options.baseUrl ?? DEFAULT_BASE_URL;
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const identityHeaders =
    options.identityHeaders ?? ((): Record<string, string> => ({}));

  function report(error: ApiError): void {
    if (!options.onAuthEvent) {
      return;
    }
    if (error.isUnauthenticated) {
      options.onAuthEvent("unauthenticated");
    } else if (error.isAccountPending || error.isAccountDisabled) {
      options.onAuthEvent("account_blocked");
    }
  }

  async function request(
    path: string,
    { method = "GET", body, query, signal }: RequestOptions = {},
  ): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...identityHeaders(),
    };
    if (MUTATING_METHODS.has(method)) {
      headers[CSRF_HEADER] = "1";
    }
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    const init: RequestInit = { method, headers, credentials: "include" };
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }
    if (signal) {
      init.signal = signal;
    }
    const response = await doFetch(buildUrl(baseUrl, path, query), init);
    if (!response.ok) {
      const error = await toApiError(response);
      report(error);
      throw error;
    }
    return response;
  }

  async function requestJson<T>(
    path: string,
    init?: RequestOptions,
  ): Promise<T> {
    const response = await request(path, init);
    return (await response.json()) as T;
  }

  /** Tolerates the documented "no body" answer of DELETE/action routes. */
  async function requestOptionalJson<T>(
    path: string,
    init?: RequestOptions,
  ): Promise<T | null> {
    const response = await request(path, init);
    const text = await response.text();
    return text.trim() ? (JSON.parse(text) as T) : null;
  }

  const dataset = (id: string): string =>
    `/datasets/${encodeURIComponent(id)}`;
  const version = (id: string, number: number): string =>
    `${dataset(id)}/versions/${encodeURIComponent(String(number))}`;

  return {
    getAuthConfig: (signal) =>
      requestJson<AuthConfig>("/auth/config", { signal }),

    logout: async () => {
      await request("/auth/logout", { method: "POST" });
    },

    getConfig: (signal) => requestJson<ConsoleConfig>("/config", { signal }),

    getMe: (signal) => requestJson<Me>("/me", { signal }),

    updateMe: (payload) =>
      requestJson<Me>("/me", { method: "PUT", body: payload }),

    enrolSecondFactor: () =>
      requestJson<SecondFactorEnrolment>("/me/second-factor", {
        method: "POST",
      }),

    verifySecondFactor: (payload) =>
      requestJson<SecondFactorConfirmed>("/me/second-factor/verify", {
        method: "POST",
        body: payload,
      }),

    removeSecondFactor: async (payload) => {
      await request("/me/second-factor", { method: "DELETE", body: payload });
    },

    listConsoleKeys: (signal) =>
      requestJson<ConsoleKeyList>("/me/keys", { signal }),

    createConsoleKey: (payload) =>
      requestJson<CreatedConsoleKey>("/me/keys", {
        method: "POST",
        body: payload,
      }),

    revokeConsoleKey: async (id) => {
      await request(`/me/keys/${encodeURIComponent(id)}`, { method: "DELETE" });
    },

    listAccounts: (query, signal) =>
      requestJson<AccountList>("/accounts", {
        query: { after: query?.after, limit: query?.limit },
        signal,
      }),

    createAccount: (payload) =>
      requestJson<Account>("/accounts", { method: "POST", body: payload }),

    getAccount: (id, signal) =>
      requestJson<Account>(`/accounts/${encodeURIComponent(id)}`, { signal }),

    listDatasets: (query, signal) =>
      requestJson<DatasetList>("/datasets", {
        query: {
          account: query?.account,
          after: query?.after,
          limit: query?.limit,
        },
        signal,
      }),

    createDataset: (payload) =>
      requestJson<Dataset>("/datasets", { method: "POST", body: payload }),

    getDataset: (id, eventsAfter, signal) =>
      requestJson<Dataset>(dataset(id), {
        query: { events_after: eventsAfter },
        signal,
      }),

    updateDataset: (id, payload) =>
      requestJson<Dataset>(dataset(id), { method: "PATCH", body: payload }),

    listEvents: (id, eventsAfter, signal) =>
      requestJson<DatasetEventList>(`${dataset(id)}/events`, {
        query: { events_after: eventsAfter },
        signal,
      }),

    listConsent: (id, signal) =>
      requestJson<ConsentList>(`${dataset(id)}/consent`, { signal }),

    recordConsent: (id, payload) =>
      requestJson<Consent>(`${dataset(id)}/consent`, {
        method: "POST",
        body: payload,
      }),

    listTargets: (id, signal) =>
      requestJson<TargetList>(`${dataset(id)}/target`, { signal }),

    recordTarget: (id, payload) =>
      requestJson<Target>(`${dataset(id)}/target`, {
        method: "POST",
        body: payload,
      }),

    listEvaluations: (id, signal) =>
      requestJson<EvaluationList>(`${dataset(id)}/evaluations`, { signal }),

    recordEvaluation: (id, payload) =>
      requestJson<Evaluation>(`${dataset(id)}/evaluations`, {
        method: "POST",
        body: payload,
      }),

    listVersions: (id, signal) =>
      requestJson<VersionList>(`${dataset(id)}/versions`, { signal }),

    getVersion: (id, number, signal) =>
      requestJson<Version>(version(id, number), { signal }),

    createVersion: (id, payload) =>
      requestJson<Version>(`${dataset(id)}/versions`, {
        method: "POST",
        body: payload,
      }),

    completeVersion: (id, number, payload) =>
      requestJson<Version>(`${version(id, number)}/complete`, {
        method: "POST",
        body: payload,
      }),

    requestAccess: (id, number, payload) =>
      requestJson<AccessGrant>(`${version(id, number)}/access`, {
        method: "POST",
        body: payload,
      }),

    previewAccess: (id, number, payload) =>
      requestJson<AccessPreview>(`${version(id, number)}/access/preview`, {
        method: "POST",
        body: payload,
      }),

    getFetchLines: (id, number, purpose, signal) =>
      requestJson<FetchLines>(`${version(id, number)}/fetch-lines`, {
        query: { purpose },
        signal,
      }),

    listReads: (id, after, signal) =>
      requestJson<ReadList>(`${dataset(id)}/reads`, {
        query: { after },
        signal,
      }),

    requestDeletion: (id, payload) =>
      requestJson<DeletionRequest>(dataset(id), {
        method: "DELETE",
        body: payload,
      }),

    getDeletion: (id, signal) =>
      requestJson<DeletionRequest>(`${dataset(id)}/deletion`, { signal }),

    cancelDeletion: async (id) => {
      await request(`${dataset(id)}/deletion`, { method: "DELETE" });
    },

    listUsers: (signal) =>
      requestJson<AdminUserList>("/admin/users", { signal }),

    approveUser: (username) =>
      requestOptionalJson<AdminUser>(
        `/admin/users/${encodeURIComponent(username)}/approve`,
        { method: "POST" },
      ),

    disableUser: (username, payload) =>
      requestOptionalJson<AdminUser>(
        `/admin/users/${encodeURIComponent(username)}/disable`,
        { method: "POST", body: payload },
      ),

    getAudit: (limit = 200, after, signal) =>
      requestJson<AuditList>("/admin/audit", {
        query: { limit, after },
        signal,
      }),

    setPaused: (payload) =>
      requestOptionalJson<PauseState>("/admin/pause", {
        method: "POST",
        body: payload,
      }),

    getStorageHealth: (signal) =>
      requestJson<StorageHealth>("/admin/storage", { signal }),
  };
}
